import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import StreakDisplay from './StreakDisplay';
import { getRankInfo } from '../utils/rankUtils';
import { supabase } from '../lib/supabase';
import SkeletonLoader from './SkeletonLoader';
import './Dashboard.css';

interface DashboardProps {
  onNavigate: (page: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onNavigate }) => {
  const { user, checkDailyLogin } = useAuth();
  const [showStreakRewards, setShowStreakRewards] = useState(false);
  const [recentActivities, setRecentActivities] = useState<any[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);

  useEffect(() => {
    // Only check daily login once per session to avoid duplicate achievement checks
    const hasCheckedToday = sessionStorage.getItem('hasCheckedDailyLogin');
    const today = new Date().toDateString();
    
    if (hasCheckedToday !== today) {
      checkDailyLogin();
      sessionStorage.setItem('hasCheckedDailyLogin', today);
    }
  }, [checkDailyLogin]);

  useEffect(() => {
    fetchRecentActivities();
  }, []);

  const fetchRecentActivities = async () => {
    try {
      // First get activities
      const { data: activities, error } = await supabase
        .from('activities')
        .select('*')
        .eq('status', 'approved')
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) {
        setRecentActivities([]);
        return;
      }

      if (!activities || activities.length === 0) {
        setRecentActivities([]);
        return;
      }

      // Get unique profile IDs
      const profileIds = [...new Set(activities.map(a => a.profile_id))];
      
      // Fetch profiles separately
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, name')
        .in('id', profileIds);

      // Create a map of profile ID to name
      const profileMap = new Map(profiles?.map(p => [p.id, p.name]) || []);

      // Merge the data
      const activitiesWithProfiles = activities.map(activity => ({
        ...activity,
        profiles: { name: profileMap.get(activity.profile_id) || 'Anonymous' }
      }));

      setRecentActivities(activitiesWithProfiles);
    } catch (err) {
      setRecentActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  };

  if (!user) return null;

  const rankInfo = getRankInfo(user.rank);

  return (
    <div className="dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="user-info profile-link" onClick={() => onNavigate('profile')} title="View Profile">
            <div className="avatar">
              {user.avatarUrl ? (
                <img src={user.avatarUrl} alt={user.name} className="avatar-img" />
              ) : (
                '👤'
              )}
            </div>
            <div>
              <h2>{user.name}</h2>
              <p className="roll-no">{user.rollNo}</p>
              <p className="department">{user.department}</p>
              <p className="profile-hint">View Profile</p>
            </div>
            <div className="profile-arrow">→</div>
          </div>
          <div className="points-display">
            <p>Your Points</p>
            <h3>{user.points}</h3>
          </div>
        </div>
        
      </div>

      {/* Streak Display */}
      <div className="dashboard-content">
        <StreakDisplay 
          streak={user.loginStreak || 0} 
          lastActivity={user.lastLoginDate} 
        />
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card" onClick={() => onNavigate('achievements')} style={{ cursor: 'pointer' }}>
          <div className="stat-icon">🏆</div>
          <div className="stat-value">{user.achievements.length}</div>
          <div className="stat-label">Achievements</div>
        </div>
        <div className="stat-card" onClick={() => onNavigate('ranks')} style={{ cursor: 'pointer' }}>
          <div className="stat-icon">{rankInfo.icon}</div>
          <div className="stat-value">{rankInfo.name}</div>
          <div className="stat-label">Your Rank</div>
        </div>
        <div className="stat-card" onClick={() => setShowStreakRewards(true)} style={{ cursor: 'pointer' }}>
          <div className="stat-icon">📅</div>
          <div className="stat-value">{user.loginStreak}</div>
          <div className="stat-label">Login Streak</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🎯</div>
          <div className="stat-value">{user.totalActivities}</div>
          <div className="stat-label">Activities</div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="action-buttons">
        <button 
          className="action-btn primary"
          onClick={() => onNavigate('camera')}
        >
          <span className="btn-icon">📸</span>
          Start Activity
        </button>
        
        <button 
          className="action-btn secondary"
          onClick={() => onNavigate('leaderboard')}
        >
          <span className="btn-icon">🏆</span>
          View Leaderboard
        </button>
        
        <button 
          className="action-btn rewards"
          onClick={() => onNavigate('rewards')}
        >
          <span className="btn-icon">🎁</span>
          Rewards Store
        </button>

        <button 
          className="action-btn feed"
          onClick={() => onNavigate('feed')}
        >
          <span className="btn-icon">📱</span>
          Activity Feed
        </button>
        
        {user.isAdmin && (
          <button 
            className="action-btn admin"
            onClick={() => onNavigate('admin')}
          >
            <span className="btn-icon">🛡️</span>
            Admin Panel
          </button>
        )}
        
        <button 
          className="action-btn game"
          onClick={() => onNavigate('game')}
        >
          <span className="btn-icon">🎮</span>
          Flappy Campus
        </button>

        <button 
          className="action-btn issue-tracker"
          onClick={() => onNavigate('issue-tracker')}
        >
          <span className="btn-icon">🚨</span>
          Issue Tracker
        </button>
        
        <button 
          className="action-btn bug-report"
          onClick={() => onNavigate('bug-report')}
        >
          <span className="btn-icon">🐛</span>
          Report Bug/Suggestion
        </button>
      </div>


      {/* Recent Activity */}
      <div className="recent-activity">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h3>Recent Campus Activity</h3>
          <button 
            onClick={() => onNavigate('activities')}
            style={{ 
              background: '#10b981', 
              color: 'white', 
              border: 'none',
              padding: '0.375rem 0.75rem',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            View All
          </button>
        </div>
        <div className="activity-list">
          {activitiesLoading ? (
            <div style={{ padding: '1rem' }}>
              {[1, 2, 3].map((index) => (
                <div key={index} className="activity-item" style={{ marginBottom: '1rem' }}>
                  <SkeletonLoader type="avatar" />
                  <div className="activity-content" style={{ flex: 1 }}>
                    <SkeletonLoader type="title" width="60%" />
                    <SkeletonLoader type="text" width="40%" />
                    <SkeletonLoader type="text" width="30%" />
                  </div>
                </div>
              ))}
            </div>
          ) : recentActivities.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
              No recent activities yet. Be the first to contribute!
            </div>
          ) : (
            recentActivities.map((activity) => (
              <div key={activity.id} className="activity-item">
                <div className="activity-icon">
                  {activity.type === 'video' ? '🎥' : '📸'}
                </div>
                <div className="activity-content">
                  <h4>{activity.location || 'Campus Area'}</h4>
                  <p>
                    {activity.profiles?.name || 'A student'} • {activity.points_earned || 0} points
                  </p>
                  <span className="activity-time">
                    {new Date(activity.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Login Streak Rewards Modal */}
      {showStreakRewards && (
        <div className="modal-overlay" onClick={() => setShowStreakRewards(false)}>
          <div className="streak-rewards-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔥 Login Streak Rewards</h2>
              <button className="close-btn" onClick={() => setShowStreakRewards(false)}>×</button>
            </div>
            <div className="modal-content">
              <div className="current-streak">
                <h3>Your Current Streak</h3>
                <div className="streak-display">
                  <span className="streak-number">{user.loginStreak}</span>
                  <span className="streak-label">days</span>
                </div>
              </div>
              
              <h3>Streak Milestones & Badges</h3>
              <div className="streak-milestones">
                <div className={`milestone ${user.loginStreak >= 3 ? 'achieved' : ''}`}>
                  <div className="milestone-badge">🥉</div>
                  <div className="milestone-info">
                    <h4>Early Bird</h4>
                    <p>3 days streak</p>
                    <span className="milestone-points">+10 points</span>
                  </div>
                </div>
                
                <div className={`milestone ${user.loginStreak >= 7 ? 'achieved' : ''}`}>
                  <div className="milestone-badge">🥈</div>
                  <div className="milestone-info">
                    <h4>Week Warrior</h4>
                    <p>7 days streak</p>
                    <span className="milestone-points">+25 points</span>
                  </div>
                </div>
                
                <div className={`milestone ${user.loginStreak >= 14 ? 'achieved' : ''}`}>
                  <div className="milestone-badge">🥇</div>
                  <div className="milestone-info">
                    <h4>Fortnight Fighter</h4>
                    <p>14 days streak</p>
                    <span className="milestone-points">+50 points</span>
                  </div>
                </div>
                
                <div className={`milestone ${user.loginStreak >= 30 ? 'achieved' : ''}`}>
                  <div className="milestone-badge">💎</div>
                  <div className="milestone-info">
                    <h4>Monthly Master</h4>
                    <p>30 days streak</p>
                    <span className="milestone-points">+100 points</span>
                  </div>
                </div>
                
                <div className={`milestone ${user.loginStreak >= 60 ? 'achieved' : ''}`}>
                  <div className="milestone-badge">👑</div>
                  <div className="milestone-info">
                    <h4>Commitment King</h4>
                    <p>60 days streak</p>
                    <span className="milestone-points">+200 points</span>
                  </div>
                </div>
                
                <div className={`milestone ${user.loginStreak >= 100 ? 'achieved' : ''}`}>
                  <div className="milestone-badge">🌟</div>
                  <div className="milestone-info">
                    <h4>Century Champion</h4>
                    <p>100 days streak</p>
                    <span className="milestone-points">+300 points</span>
                  </div>
                </div>
                
                <div className={`milestone ${user.loginStreak >= 150 ? 'achieved' : ''}`}>
                  <div className="milestone-badge">🔥</div>
                  <div className="milestone-info">
                    <h4>Unstoppable Force</h4>
                    <p>150 days streak</p>
                    <span className="milestone-points">+400 points</span>
                  </div>
                </div>
                
                <div className={`milestone ${user.loginStreak >= 200 ? 'achieved' : ''}`}>
                  <div className="milestone-badge">⚡</div>
                  <div className="milestone-info">
                    <h4>Lightning Legend</h4>
                    <p>200 days streak</p>
                    <span className="milestone-points">+500 points</span>
                  </div>
                </div>
                
                <div className={`milestone ${user.loginStreak >= 250 ? 'achieved' : ''}`}>
                  <div className="milestone-badge">🚀</div>
                  <div className="milestone-info">
                    <h4>Space Explorer</h4>
                    <p>250 days streak</p>
                    <span className="milestone-points">+600 points</span>
                  </div>
                </div>
                
                <div className={`milestone ${user.loginStreak >= 300 ? 'achieved' : ''}`}>
                  <div className="milestone-badge">💫</div>
                  <div className="milestone-info">
                    <h4>Cosmic Cleaner</h4>
                    <p>300 days streak</p>
                    <span className="milestone-points">+750 points</span>
                  </div>
                </div>
                
                <div className={`milestone ${user.loginStreak >= 365 ? 'achieved' : ''}`}>
                  <div className="milestone-badge">🏆</div>
                  <div className="milestone-info">
                    <h4>Year Champion</h4>
                    <p>365 days streak - Full Year!</p>
                    <span className="milestone-points">+1000 points</span>
                  </div>
                </div>
              </div>
              
              <div className="streak-tip">
                <p>💡 <strong>Tip:</strong> Login daily to maintain your streak and earn bonus points!</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Footer Credits */}
      <div className="dashboard-footer">
        <div className="footer-logos">
          <img src="/images/byte-bandit.jpg" alt="ByteBandits" className="footer-logo" />
          <span className="footer-separator">×</span>
          <img src="/images/uet-logo.png" alt="UET" className="footer-logo" />
        </div>
        <p>Developed by <a href="https://www.instagram.com/byt_ebandits/" target="_blank" rel="noopener noreferrer" className="bytebandits-link"><strong>ByteBandits</strong></a> • UET</p>
        <a href="https://www.instagram.com/byt_ebandits/" target="_blank" rel="noopener noreferrer" className="instagram-follow-btn">
          <svg className="instagram-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1 1 12.324 0 6.162 6.162 0 0 1-12.324 0zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm4.965-10.405a1.44 1.44 0 1 1 2.881.001 1.44 1.44 0 0 1-2.881-.001z"/>
          </svg>
          <span>Follow us on Instagram</span>
        </a>
      </div>
    </div>
  );
};

export default Dashboard;